<template>
  <BaseModal ref="modal" type="modal" :closeButtonVisible="closeButtonVisible">
    <template #body>
      <div class="dialog-container">
        <div class="title" :class="[type]">
          <BaseSvg
            v-if="type"
            class="dialog-icon"
            :class="[type]"
            :src="iconMap[type]"
          />
          {{ title }}
        </div>

        <div class="description-container">
          <p v-for="(description, index) in descriptions" :key="index">
            {{ description }}
          </p>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="button-container">
        <BaseButton
          v-if="secondaryButtonData.label"
          :size="secondaryButtonData?.size"
          :variant="secondaryButtonData?.type"
          :text="secondaryButtonData?.label"
          :loading="secondaryButtonLoading"
          :ghost="secondaryButtonData?.ghost"
          :bordered="secondaryButtonData?.bordered"
          @on-click="secondaryClickHandler"
        />
        <BaseButton
          v-if="buttonData?.label"
          :size="buttonData?.size"
          :variant="buttonData?.type"
          :text="buttonData.label"
          :disabled="buttonData?.isDisabled"
          :loading="buttonLoading"
          :ghost="buttonData?.ghost"
          :bordered="buttonData?.bordered"
          @on-click="clickHandler"
        />
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { BaseModal, BaseButton, BaseSvg } from '@/components/Base';
import { computed, ref, useTemplateRef } from 'vue';
import type { IBaseDialogModal } from '@/types/components';
import { SuccessIcon, WarningIcon, ErrorIcon, InfoOutline } from '@/assets/icons';
const props = withDefaults(defineProps<IBaseDialogModal>(), {
  descriptions: () => [],
  type: '',
});

const iconMap = {
  success: SuccessIcon,
  warning: WarningIcon,
  error: ErrorIcon,
  info: InfoOutline,
};

const buttonLoading = ref(false);
const secondaryButtonLoading = ref(false);
const modalRef = useTemplateRef<InstanceType<typeof BaseModal>>('modal');

const buttonData = computed(() => {
  return {
    label: 'Tamam',
    type: props?.actionButton?.type || 'primary',
    size: props?.actionButton?.size || 'large',
    ...(props?.actionButton || {}),
  };
});
const secondaryButtonData = computed(() => {
  return {
    type: props?.secondaryButton?.type || 'primary',
    ghost: props?.secondaryButton?.ghost ?? true,
    size: props?.secondaryButton?.size || 'large',
    ...(props?.secondaryButton || {}),
  };
});

async function clickHandler() {
  buttonLoading.value = true;
  if (typeof props?.actionButton?.action === 'function') {
    await props?.actionButton?.action();
  }
  buttonLoading.value = false;
  modalRef.value?.closeModal();
}
async function secondaryClickHandler() {
  secondaryButtonLoading.value = true;
  if (typeof props?.secondaryButton?.action === 'function') {
    await props?.secondaryButton?.action();
  }
  secondaryButtonLoading.value = false;
  modalRef.value?.closeModal();
}
</script>

<style scoped lang="scss">
.dialog-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
  & .title {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 24px;
    font-weight: 600;
    & .dialog-icon {
      width: 48px;
    }
    &.success {
      color: $success-200;
    }
    &.error {
      color: $danger-200;
    }
    &.warning {
      color: $warning-200;
    }
    &.info {
      color: $primary-200;
    }
  }
  & .description-container {
    display: flex;
    flex-direction: column;
    font-size: 18px;
  }
}
.button-container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}
</style>
